import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from './api';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

// Subscribing mints a real endpoint with FCM or Apple and stores it
// server-side, so a dev session leaves a live route to a real device
// behind it. Production builds subscribe; a local `npm start` does not,
// unless REACT_APP_PUSH_ENABLED=true says you mean it.
const PUSH_ENABLED = process.env.NODE_ENV === 'production'
  || process.env.REACT_APP_PUSH_ENABLED === 'true';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

export function pushSupported(): boolean {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

export function isIOSPWA(): boolean {
  if (typeof window === 'undefined') return false;
  const standalone = window.matchMedia?.('(display-mode: standalone)')?.matches
    || (window.navigator as any).standalone === true;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  return standalone && isIOS;
}

export function permissionState(): NotificationPermission | 'unsupported' {
  if (!pushSupported()) return 'unsupported';
  return Notification.permission;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      updateViaCache: 'none',
    });
    return registration;
  } catch (err) {
    console.warn('[Push] Service worker registration failed:', err);
    return null;
  }
}

async function postSubscription(subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON();
  const platform = isIOSPWA() ? 'ios-pwa' : /Android/i.test(navigator.userAgent) ? 'android-pwa' : 'web';
  await authenticatedFetch(API_ENDPOINTS.push.subscribe, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      endpoint: json.endpoint,
      keys: json.keys,
      platform,
    }),
  });
}

/**
 * Do the actual pushManager subscribe + server POST. Assumes permission is
 * already granted — callers are responsible for that check.
 */
async function subscribeAndPersist(): Promise<PushSubscription | null> {
  if (!PUSH_ENABLED) {
    console.info('[Push] Disabled outside production — not subscribing. Set REACT_APP_PUSH_ENABLED=true to override.');
    return null;
  }
  if (!pushSupported() || !VAPID_PUBLIC_KEY) {
    console.warn('[Push] not supported or VAPID key missing');
    return null;
  }

  const registration = await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    } catch (err) {
      console.warn('[Push] pushManager.subscribe failed:', err);
      return null;
    }
  }

  try {
    await postSubscription(subscription);
  } catch (err) {
    console.warn('[Push] failed to POST subscription:', err);
  }
  return subscription;
}

/**
 * Silently re-subscribe if permission is already granted. Safe to call on every app load.
 */
export async function ensureSubscribed(): Promise<PushSubscription | null> {
  if (Notification.permission !== 'granted') return null;
  return subscribeAndPersist();
}

// Samsung Internet PWA: Notification.requestPermission() can resolve with
// 'default' even after the user tapped Allow — the browser records the grant
// on its own schedule. Poll the live property for a moment before giving up.
async function waitForGrant(timeoutMs: number): Promise<NotificationPermission> {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    if (Notification.permission !== 'default') return Notification.permission;
    await new Promise(r => setTimeout(r, 100));
  }
  return Notification.permission;
}

/**
 * Explicit opt-in: prompts the user for permission, then subscribes.
 * Only call from a user gesture (button click).
 */
export async function requestPermissionAndSubscribe(): Promise<PushSubscription | null> {
  if (!pushSupported() || !VAPID_PUBLIC_KEY) {
    console.warn('[Push] not supported or VAPID key missing');
    return null;
  }

  let permission = await Notification.requestPermission();
  if (permission === 'default') {
    permission = await waitForGrant(3000);
  }
  if (permission !== 'granted') return null;

  return subscribeAndPersist();
}

export async function unsubscribe(): Promise<boolean> {
  if (!pushSupported()) return false;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return true;

  try {
    await authenticatedFetch(API_ENDPOINTS.push.unsubscribe, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint: subscription.endpoint }),
    });
  } catch (err) {
    console.warn('[Push] server unsubscribe failed:', err);
  }
  await subscription.unsubscribe();
  return true;
}
